import { useApolloClient } from '@apollo/client';
import Decimal from 'decimal.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useWatch } from 'react-hook-form';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { LoadingAdornment } from '@/components/form/baseInputs/SelectInput/LoadingAdornment';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { useFormContext } from '@/components/react-hook-form';
import { getKindFromProvider } from '@/modules/assetProviderIntegrations/assetProviderIntegrations.utils';
import {
  AssetIntegrationProviders,
  getIntegrationProviderDisplayName,
} from '@/modules/assetProviderIntegrations/shared/constants';
import { useIsAssetProviderIntegrationEnabled } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import {
  getInferNameMatchCandidatesFromOptions,
  inferNameMatch,
} from '@/modules/assetProviderIntegrations/shared/integrationInference';
import { IntegrationInputCard } from '@/modules/assetProviderIntegrations/shared/IntegrationInputCard';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';

import { useGetIntegrationLiabilitiesQuery } from '../graphql/IntegrationLiabilitySelect.generated';
import { useIssuerAndRecipientOptionsGrouped } from '../hooks/useRecipientOptions';
import {
  getLiabilityIntegrationPaymentFrequency,
  getLiabilityIntegrationPrefilledFields,
  hasLiabilityIntegrationPaymentDetails,
} from '../LiabilityForm.integration';
import {
  defaultValues,
  LiabilityFormPaths,
  LiabilityFormShape,
  NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL,
} from '../LiabilityModal.constants';

const MAX_OPTIONS = 50;

interface IntegrationLiabilitySelectProps {
  provider: AssetIntegrationProviders;
  onPrefill?: (fields: Set<keyof LiabilityFormShape>) => void;
}

export function IntegrationLiabilitySelect({
  provider,
  onPrefill,
}: IntegrationLiabilitySelectProps) {
  const apolloClient = useApolloClient();
  const { control, getValues, setValue } = useFormContext<LiabilityFormShape>();
  const { householdId } = useHouseholdDetailsContext();
  const { integrationsConnected } = useIsAssetProviderIntegrationEnabled();
  const { options: issuerAndRecipientOptions } =
    useIssuerAndRecipientOptionsGrouped({});
  const [isInferring, setIsInferring] = useState(false);

  const integrationClientKind = getKindFromProvider(provider);
  const providerName = getIntegrationProviderDisplayName(provider);
  const isLoggedIn = integrationsConnected.includes(provider);

  const selectedId = useWatch({ control, name: 'integrationLiabilityId' });

  const { data, previousData } = useGetIntegrationLiabilitiesQuery({
    variables: {
      optionsLike: {
        hasIntegrationClientWith: [
          {
            kind: integrationClientKind,
            hasClientHouseholdsWith: [{ id: householdId! }],
          },
        ],
      },
      selectedOptionsLike: { idIn: selectedId ? [selectedId] : [''] },
      first: MAX_OPTIONS,
    },
    skip: !householdId,
  });

  const currentData = data ?? previousData;

  const allNodes = useMemo(() => {
    const queryNodes =
      currentData?.queryData?.edges
        ?.map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => n != null) ?? [];
    const selectedNodes =
      currentData?.selectedData?.edges
        ?.map((e) => e?.node)
        .filter((n): n is NonNullable<typeof n> => n != null) ?? [];

    // Merge selected node if not already in the query results
    const merged = [...queryNodes];
    for (const selected of selectedNodes) {
      if (!merged.some((n) => n.id === selected.id)) {
        merged.push(selected);
      }
    }
    return merged;
  }, [currentData]);

  const options: TypeaheadSelectInputOption<string>[] = useMemo(() => {
    return allNodes.map((node) => {
      const valueLabel =
        'value' in node && node.value != null
          ? ` (${formatCurrencyNoDecimalsAccounting(new Decimal(node.value))})`
          : '';
      return {
        value: node.id,
        display: `${node.name}${valueLabel}`,
        groupName: node.integrationClient?.name ?? providerName,
      };
    });
  }, [allNodes, providerName]);

  const selectedLiability = useMemo(
    () => allNodes.find((node) => node.id === selectedId),
    [allNodes, selectedId]
  );
  const nameMatchCandidates = useMemo(
    () => getInferNameMatchCandidatesFromOptions(issuerAndRecipientOptions),
    [issuerAndRecipientOptions]
  );

  const selectedLiabilityPrefilledFields = useMemo(() => {
    if (!selectedLiability) return null;

    return getLiabilityIntegrationPrefilledFields({
      name: selectedLiability.name,
      balanceAsOfDate: new Date(),
      outstandingBalance: selectedLiability.value,
      paymentAmount: selectedLiability.recurringPaymentAmount,
      paymentFrequency: getLiabilityIntegrationPaymentFrequency(
        selectedLiability.paymentFrequency
      ),
      interestRate: selectedLiability.interestRate,
      termStartDate: selectedLiability.termStart,
      termEndDate: selectedLiability.termEnd,
    });
  }, [selectedLiability]);

  // Prefill form fields when an integration liability is selected.
  useEffect(() => {
    if (!selectedLiabilityPrefilledFields) return;

    onPrefill?.(selectedLiabilityPrefilledFields);
  }, [selectedLiabilityPrefilledFields, onPrefill]);

  const prevSelectedIdRef = useRef(selectedId);
  useEffect(() => {
    if (!selectedLiability) return;
    if (selectedId === prevSelectedIdRef.current) return;

    prevSelectedIdRef.current = selectedId;

    setValue('name', selectedLiability.name);
    setValue('balanceAsOfDate', new Date());
    setValue(
      'outstandingBalance',
      selectedLiability.value != null
        ? new Decimal(selectedLiability.value)
        : defaultValues.outstandingBalance
    );
    const paymentFrequency = getLiabilityIntegrationPaymentFrequency(
      selectedLiability.paymentFrequency
    );
    setValue(
      '_showExpandedPaymentDetails',
      hasLiabilityIntegrationPaymentDetails(
        selectedLiabilityPrefilledFields ?? new Set()
      )
    );
    setValue(
      'paymentAmount',
      selectedLiability.recurringPaymentAmount ?? defaultValues.paymentAmount
    );
    setValue(
      'paymentFrequency',
      paymentFrequency ?? defaultValues.paymentFrequency
    );
    setValue(
      'interestRate',
      selectedLiability.interestRate ?? defaultValues.interestRate
    );
    setValue(
      'termStartDate',
      selectedLiability.termStart ?? defaultValues.termStartDate
    );
    setValue(
      'termEndDate',
      selectedLiability.termEnd ?? defaultValues.termEndDate
    );
    setValue('recipientId', NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL);

    const selectedLiabilityId = selectedLiability.id;
    let isCancelled = false;

    const inferRecipient = async () => {
      queueMicrotask(() => {
        if (!isCancelled) setIsInferring(true);
      });
      try {
        const recipientId = await inferNameMatch({
          client: apolloClient,
          name: selectedLiability.recipientName,
          candidates: nameMatchCandidates,
        });

        if (
          isCancelled ||
          getValues('integrationLiabilityId') !== selectedId ||
          selectedLiability.id !== selectedLiabilityId ||
          getValues('recipientId') !==
            NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL
        ) {
          return;
        }

        setValue(
          'recipientId',
          recipientId ?? NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL
        );
      } finally {
        if (!isCancelled) setIsInferring(false);
      }
    };

    void Promise.allSettled([inferRecipient().catch(() => undefined)]);
    return () => {
      isCancelled = true;
    };
  }, [
    apolloClient,
    getValues,
    nameMatchCandidates,
    selectedId,
    selectedLiability,
    selectedLiabilityPrefilledFields,
    setValue,
  ]);

  return (
    <IntegrationInputCard providerName={providerName}>
      <FormAwareTypeaheadSelectInput<LiabilityFormShape>
        control={control}
        label={`${providerName} liability`}
        hideLabel
        fieldName={
          'integrationLiabilityId' as const satisfies LiabilityFormPaths
        }
        options={options}
        groupBy={(option) => option.groupName ?? ''}
        placeholder="Select an integration liability to link"
        startAdornment={isInferring ? <LoadingAdornment /> : undefined}
        helpText={
          isLoggedIn
            ? undefined
            : `You have not linked your ${providerName} account. Go to manage profile under user settings to link.`
        }
        disabled={!isLoggedIn}
      />
    </IntegrationInputCard>
  );
}
